import {
  ButtonComponent,
  ButtonPriority,
  ButtonSize,
} from '@vaad1st/component.atom.button';
import {
  TypographyComponent,
  TypographySize,
  TypographyVariant,
  TypographyWeight,
} from '@vaad1st/component.atom.typography';
import { ContainerComponent } from '@vaad1st/component.layout.container';
import {
  HotelCardComponent,
  HotelCardProps,
} from '@vaad1st/component.molecule.hotel-card';
import {
  CarouselComponent as Carousel,
  CarouselConfiguration,
} from '@vaad1st/component.organism.carousel';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getHotelData, getLocationData } from 'src/utils/component-utils/utils';
import { HeadingElementType } from 'src/utils/shared-types';
import loadingImg from '../../assets/images/loading-hotel.png';
import SkipLinksComponent from '../skip-links/skip-links.component';
import style from './hotels-carousel.component.module.less';

interface HotelsCarouselComponentProps {
  mimId: string;
  locationDescription?: string;
  hotels: HotelCardProps[];
  viewAllLink?: string;
  clientSide?: boolean;
  titleOverride?: string;
  titleElement?: HeadingElementType;
  extendedPadding?: boolean;
}

const loadingHotel: HotelCardProps = {
  loading: true,
  image: {
    url: loadingImg.src,
    alt: 'Hotel loading...',
  },
  name: 'Hotel name loading...',
  location: 'Location loading...',
  tripAdvisor: {
    rating: 0,
    reviews: 0,
  },
  virgin: {
    rating: '0',
  },
  details: [
    'Detail 1 loading...',
    'Detail 2 loading...',
    'Detail 3 loading...',
    'Detail 4 loading...',
  ],
  link: {
    url: '#',
    label: 'View details',
  },
};

export default function HotelsCarouselComponent({
  hotels = [],
  locationDescription = '',
  viewAllLink,
  clientSide = false,
  mimId,
  titleOverride,
  titleElement = 'h2',
  extendedPadding = true,
}: HotelsCarouselComponentProps) {
  const loadingHotels = useMemo(
    () => Array.from({ length: 3 }, () => loadingHotel),
    [],
  );
  const [hotelsData, setHotelsData] = useState<HotelCardProps[]>(hotels);
  const [locationName, setLocationName] = useState<string>(locationDescription);

  const content = useMemo(
    () => ({
      title: locationName ? `Hotels in ${locationName}` : 'Available hotels',
      skipLinks: locationName
        ? `Skip hotel cards for ${locationName}`
        : 'Skip hotel cards',
    }),
    [locationName],
  );

  let updatedViewAllLink = viewAllLink ?? undefined;

  const fetchHotelsData = useCallback(
    async (locationId: string) => {
      const location = await getLocationData(locationId);
      if (location) {
        const hotelsObj = await getHotelData(location, true);
        if (!hotelsObj?.hotels) {
          return;
        }
        if (
          hotelsObj.hotels &&
          hotelsObj.hotels.length > 0 &&
          hotelsData.length === 0
        ) {
          setTimeout(() => {
            setHotelsData(hotelsObj.hotels);
            setLocationName(hotelsObj.location);
          }, 1000);
        }
      }
    },
    [hotelsData.length],
  );

  useEffect(() => {
    if (clientSide) {
      /// Load hotels carousel client side
      if (hotelsData.length === 0) {
        setHotelsData(loadingHotels);
      }
      try {
        fetchHotelsData(mimId);
      } catch (e) {
        console.log('Error loading hotels carousel client side', e);
      }
    }
  }, [clientSide, hotelsData.length, loadingHotels, mimId, fetchHotelsData]);

  if (!hotelsData || hotelsData.length === 0) {
    return null;
  }

  const handleFocus = (e: React.FocusEvent) => {
    const button = document.querySelector(
      '#view-all-hotels .button-component',
    ) as HTMLElement;
    if (button) {
      button.focus();
    }
  };

  return (
    <div
      className={classNames(style['hotels-carousel'], {
        [style['carousel--extended-padding']]: extendedPadding,
      })}
      id="hotels-carousel"
      tabIndex={-1}>
      <SkipLinksComponent
        skipForwards={{
          targetId: 'view-all-hotels-focus',
          label: content.skipLinks,
        }}
        skipBackwards={{
          label: content.skipLinks,
        }}>
        <ContainerComponent as="div">
          <TypographyComponent
            variant={TypographyVariant.Heading}
            size={TypographySize.Lrg}
            as={titleElement}>
            {titleOverride || content.title}
          </TypographyComponent>
        </ContainerComponent>
        <Carousel
          animateIn={false}
          configuration={CarouselConfiguration.Heavy}
          extendedXlLayout={true}
          controlsConfig={{
            arrows: hotelsData.length > 3,
            byScreen: true,
            byDrag: true,
            hideInactive: true,
            smallButtons: false,
            lazyLoading: false,
          }}>
          {hotelsData.map((hotel: HotelCardProps, index: number) => (
            <HotelCardComponent key={`hotel--${index}`} {...hotel} />
          ))}
        </Carousel>
      </SkipLinksComponent>
      {viewAllLink && hotelsData.length > 3 && (
        <div id="view-all-hotels" className={style.viewAllHotels} tabIndex={-1}>
          <ButtonComponent
            as="a"
            text={
              hotelsData.length > 0
                ? `View all ${hotelsData.length} places to stay`
                : 'View all places to stay'
            }
            href={updatedViewAllLink}
            size={ButtonSize.Small}
            priority={ButtonPriority.Tertiary}
          />
          <div id="view-all-hotels-focus" tabIndex={-1} onFocus={handleFocus} />
        </div>
      )}
    </div>
  );
}
